import React, { useState, useEffect } from 'react';
import { getOrderFiles, downloadFile, getDownloadUrl } from '../services/downloadService';

interface DownloadFilesModalProps {
  orderId: string;
  isOpen: boolean;
  onClose: () => void;
}

interface FileWithProgress {
  name: string;
  size: string;
  type: string;
  url?: string;
  progress?: number;
  isDownloading?: boolean;
}

const DownloadFilesModal: React.FC<DownloadFilesModalProps> = ({ orderId, isOpen, onClose }) => {
  const [files, setFiles] = useState<FileWithProgress[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      // Fetch files when modal opens
      setLoading(true);
      const orderFiles = getOrderFiles(orderId);
      setFiles(orderFiles);
      setLoading(false);
    }
  }, [orderId, isOpen]);

  const handleDownload = (file: FileWithProgress) => {
    // Set this file as downloading with 0 progress
    setFiles(prev =>
      prev.map(f =>
        f.name === file.name
          ? { ...f, isDownloading: true, progress: 0 }
          : f
      )
    );

    // Get download URL
    const downloadUrl = getDownloadUrl(file.name, orderId);

    // In a real app, you would track download progress here
    // For this mock, we'll simulate progress
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;

      if (progress <= 100) {
        setFiles(prev =>
          prev.map(f =>
            f.name === file.name
              ? { ...f, progress }
              : f
          )
        );
      } else {
        clearInterval(interval);
        // After download completes
        setFiles(prev =>
          prev.map(f =>
            f.name === file.name
              ? { ...f, isDownloading: false }
              : f
          )
        );

        // Trigger actual download
        downloadFile(file.name, downloadUrl);
      }
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1F1A27] rounded-lg w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 bg-[#2A2633] flex justify-between items-center">
          <h2 className="text-xl font-bold">Download Files</h2>
          <button
            onClick={onClose}
            className="text-[var(--color-muted)] hover:text-white"
            aria-label="Close"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-grow">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#7F56D9]"></div>
            </div>
          ) : files.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-[var(--color-muted)]">No files available for download.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {files.map((file, index) => (
                <div
                  key={index}
                  className="p-4 bg-[#2A2633] rounded-lg flex items-center justify-between"
                >
                  <div className="flex items-center">
                    <div className="mr-3 p-2 bg-[#3A3643] rounded">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" xmlns="http://www.w3.org/2000/svg" className="text-[#7F56D9]">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                      </svg>
                    </div>
                    <div>
                      <p className="text-white font-medium">{file.name}</p>
                      <p className="text-xs text-[var(--color-muted)]">{file.type} • {file.size}</p>
                    </div>
                  </div>

                  {file.isDownloading ? (
                    <div className="w-32">
                      <div className="h-1 w-full bg-[#3A3643] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#7F56D9] transition-all duration-300"
                          style={{ width: `${file.progress}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-right mt-1 text-[var(--color-muted)]">
                        {file.progress}%
                      </p>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleDownload(file)}
                      className="py-2 px-4 bg-[#7F56D9] hover:bg-[#7F56D9]/80 text-white text-sm rounded-md transition-colors flex items-center"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" xmlns="http://www.w3.org/2000/svg" className="mr-2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"></path>
                      </svg>
                      Download
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-[#3A3643]">
          <p className="text-sm text-[var(--color-muted)] mb-4">
            Files are available for download for 30 days after purchase.
            Contact support if you need to download files after this period.
          </p>
          <button
            onClick={onClose}
            className="w-full py-2 border border-[#3A3643] text-white rounded-md hover:bg-[#2A2633] transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DownloadFilesModal;
